import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@/shared/api/api-client';
import { invalidateIfNotInFlight } from '@/patients/lib/invalidate-if-not-in-flight';
import type { UpdateAllergyBody } from '@/types/patient-chart-api';

/**
 * PUT /api/Patients/allergy/{id}
 *
 * Single endpoint for BOTH Edit and Delete on Allergies. Soft-delete
 * just flips `isDeleted: true` in the otherwise-unchanged body. Wire
 * body curl-verified by Andrew 2026-05-19.
 *
 * Mirrors Flutter `patient_chart_data_source_impl.dart` `updateAllergy()`.
 *
 * Body shape: see `UpdateAllergyBody` type doc — note the round-trip
 * shape differences from `CreateAllergyBody`:
 * - `identifier` is the existing GUID (not null).
 * - `category` is top-level (not nested under code).
 * - `code` is plain `CodeWordCase` (no nested category).
 * - `recorder` stays the ORIGINAL recording user, not the current
 *   editor (curl-verified — both Edit and Delete keep the original
 *   recorder id).
 * - `recordedDate`, `asserter`, `createdDate`, `performerDetails` are
 *   round-tripped from the original record.
 *
 * Date format reminder: `onset` (when present) ships as
 * `YYYY-MM-DDTHH:mm:ss.sss` without the trailing `Z` (.NET model-binder
 * convention).
 *
 * Cache: invalidates `['patient-allergies', patientId]` so the chart
 * Allergies tab refetches. The SignalR `UpdatePatientChat` "Allergy"
 * event from PROD-827 Slice 3 (PR #202) also fires for concurrent
 * sessions in other tabs / browsers.
 */

interface UpdateAllergyParams {
  patientId: string;
  /** Allergy id — must equal `body.identifier`. */
  allergyId: string;
  body: UpdateAllergyBody;
}

export function useUpdateAllergy() {
  const qc = useQueryClient();
  return useMutation<string, Error, UpdateAllergyParams>({
    mutationFn: ({ allergyId, body }) =>
      put<string>(`/api/Patients/allergy/${allergyId}`, body),
    onSuccess: (_data, { patientId }) => {
      invalidateIfNotInFlight(qc, ['patient-allergies', patientId]);
    },
  });
}
